import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ExternalContact,
  Process,
  ProcessEventNode,
  ProcessStepRecording,
  System,
  User
} from '@bliks-io/contracts';
import {
  buildProcessNodes,
  buildProcessEdges,
  type NodeBuilderData,
  type NodeBuilderHandlers
} from '../utils/nodeBuilder';
import { getLayoutedElements, styleEdges } from '../utils/layoutUtils';

interface UseProcessDiagramDataArgs {
  currentRecording: ProcessStepRecording | null;
  currentProcess: Process | null | undefined;
  /** Event nodes from the process response (ADR 0003) — start-node display source. */
  events: ProcessEventNode[] | undefined;
  orgMembers: User[] | undefined;
  systems: System[] | undefined;
  externalContacts: ExternalContact[] | undefined;
  artifacts: NodeBuilderData['artifacts'] | undefined;
  handlers: NodeBuilderHandlers;
}

/**
 * Builds the React Flow nodes/edges for the step recording diagram plus a
 * content-driven signature the diagram uses to trigger relayouts.
 */
export function useProcessDiagramData({
  currentRecording,
  currentProcess,
  events,
  orgMembers,
  systems,
  externalContacts,
  artifacts,
  handlers
}: UseProcessDiagramDataArgs) {
  const { t } = useTranslation();

  const availableSuccessors = useMemo(
    () => ({
      persons: orgMembers || [],
      systems: systems || [],
      externalContacts: externalContacts || []
    }),
    [orgMembers, systems, externalContacts]
  );

  const { layoutedNodes, styledEdges } = useMemo(() => {
    if (!currentRecording) {
      return { layoutedNodes: [], styledEdges: [] };
    }

    const nodeBuilderData: NodeBuilderData = {
      currentRecording,
      events,
      availableSuccessors,
      artifacts: artifacts || [],
      t
    };

    const rawNodes = buildProcessNodes(nodeBuilderData, handlers);
    const rawEdges = buildProcessEdges();

    if (rawNodes.length === 0) {
      return { layoutedNodes: [], styledEdges: [] };
    }

    const layouted = getLayoutedElements(rawNodes);
    const styled = styleEdges(rawEdges);

    return { layoutedNodes: layouted, styledEdges: styled };
  }, [currentRecording, events, availableSuccessors, artifacts, handlers, t]);

  // Stable signature based on data content (not function references) so
  // ProcessDiagram only reacts when the underlying recording actually changes.
  //
  // The WHOLE recording is signed, not a picked field list: React Flow owns the
  // node data once mounted, so any recording field the signature omits stays
  // invisible on the canvas until a page reload. A picked list silently drifts
  // from what the nodes render — `task.output` was missing, which swallowed
  // every Bliksy-written output. Over-signing is cheap: the sync effect keeps
  // node positions, and the recording object only changes identity on a real
  // data change (the heartbeat deliberately does not invalidate the query).
  const contentSignature = useMemo(() => {
    return JSON.stringify({
      recording: currentRecording,
      processId: currentProcess?._id,
      // Start-node display values come from the event nodes — re-render when
      // their content changes (e.g. the start event was renamed).
      events: events?.map((event) => ({
        id: event._id,
        name: event.name,
        description: event.description,
        eventType: event.eventType
      })),
      // Only the lists the nodes actually read — org functions reach the cards
      // through their own lookup queries, never through node data.
      systemsCount: systems?.length ?? 0,
      artifactsCount: artifacts?.length ?? 0,
      orgMembersCount: orgMembers?.length ?? 0
    });
  }, [
    currentRecording,
    currentProcess,
    events,
    systems?.length,
    artifacts?.length,
    orgMembers?.length
  ]);

  return { layoutedNodes, styledEdges, contentSignature };
}
